import { call, fork, put, select, takeLatest } from 'redux-saga/effects';

import {
  constants as loadConstants,
  formatters as loadFormatters,
  methods as loadMethods,
  validators as loadValidators,
} from 'utils/loads';

import { stringMaskedToNumber } from 'utils/numbers';

import { actions as errorActions } from 'state/errors';
import {
  actions as orderActions,
  selectors as orderSelectors,
  selectorsByType,
} from 'state/order';

import { selectors as settingsSelectors } from 'state/settings';
import { selectors as shipmentsSelectors } from 'state/shipments';

import keyGenerator from 'utils/keys';
import loadsSelectors from 'state/loads/selectors';

import types from 'state/loads/actionTypes';
import loadsActions from 'state/loads/actionCreators';
import { compareObjects } from 'utils/object';

/**
 * adds a load
 * gets maximum space for new loads
 * checks if max space is reached
 * fetchs quote
 * @param {*} param0
 */
function* handleLoadAdd({ payload }) {
  const { shipmentKey, loadKey = false } = payload;
  yield put(loadsActions.addLoad({ shipmentKey, loadKey }));
  yield call(handleGetMaximum);
  yield fork(isMaxReached);
  yield put(orderActions.fetchQuote());
}

/**
 * removes a load if there are more than 1
 * gets maximum space for the new loads
 * checks if max space is reached
 * fetchs quote
 * @param {*} param0
 */
function* handleLoadRemove({ payload }) {
  const { shipmentKey } = payload;
  const { loads } = yield select(shipmentsSelectors.getShipment, shipmentKey);
  if (loads && loads.length > 1) {
    yield put(loadsActions.removeLoad(payload));
    yield call(handleGetMaximum);
    yield fork(isMaxReached);
    yield put(loadsActions.validateForm());
    yield put(orderActions.fetchQuote());
    yield put(orderActions.fetchTimeWindows());
  }
}

function* handleLoadChange({ payload }) {
  //change fields
  // change data
  const { field, value, shipmentKey, loadKey } = payload;

  //   current load data
  const currentLoad = yield select((state) =>
    loadsSelectors.getLoad(state, shipmentKey, loadKey)
  );

  let newValue = value;

  //   format numerics fields
  const formattedNumericValue = loadFormatters.formatNumericValue(value);
  const valueConverted = stringMaskedToNumber(formattedNumericValue);
  newValue = valueConverted === 0 ? value : valueConverted;

  //   values to update
  const valuesToUpdate = {
    [field]: newValue,
  };

  const {
    [loadConstants.FIELDS.LOAD_TYPE]: currentLoadType,
    [loadConstants.FIELDS.PALLET_TYPE]: currentPalletType,
    [loadConstants.FIELDS.ITEM_LENGTH]: currentLenght,
    [loadConstants.FIELDS.ITEM_WIDTH]: currentWidth,
  } = currentLoad;
  // if we are updating width or length and we have a PALLET loadType
  // we also update the load PALLET_TYPE with the new type
  if (
    (field === loadConstants.FIELDS.ITEM_LENGTH ||
      field === loadConstants.FIELDS.ITEM_WIDTH) &&
    currentLoadType === loadConstants.LOAD_TYPES.PALLET
  ) {
    let newPalletType = loadConstants.PALLET_TYPES.CUSTOM_PALLET;
    const newPalletSizes = {
      [loadConstants.FIELDS.ITEM_LENGTH]: currentLenght,
      [loadConstants.FIELDS.ITEM_WIDTH]: currentWidth,
    };
    newPalletSizes[field] = newValue;
    const newPalletSizeIsEuPallet = compareObjects(
      newPalletSizes,
      loadConstants.PALLET_SIZES.EU_PALLET
    );
    const newPalletSizeIsUsPallet = compareObjects(
      newPalletSizes,
      loadConstants.PALLET_SIZES.US_PALLET
    );
    if (newPalletSizeIsEuPallet)
      newPalletType = loadConstants.PALLET_TYPES.EU_PALLET;
    if (newPalletSizeIsUsPallet)
      newPalletType = loadConstants.PALLET_TYPES.US_PALLET;
    //   if the new pallet type is different, we update also that field
    if (newPalletType !== currentPalletType)
      valuesToUpdate[loadConstants.FIELDS.PALLET_TYPE] = newPalletType;
  }
  // if we have non_pallet type we also update it
  else if (
    field === loadConstants.FIELDS.LOAD_TYPE &&
    value !== currentLoadType
  ) {
    const newNonPalletizedType = value;
    valuesToUpdate[loadConstants.FIELDS.LOAD_TYPE] = newNonPalletizedType;
  }

  yield put(
    loadsActions.changeLoadValues({
      shipmentKey,
      loadKey,
      values: valuesToUpdate,
    })
  );
  yield call(handleGetMaximum);
  yield put(loadsActions.validateForm());
  yield put(orderActions.fetchQuote());
  yield put(orderActions.fetchTimeWindows());
}

function* handleLoadType({ payload }) {
  const { field, value, shipmentKey, loadKey } = payload;

  const currentLoad = yield select((state) =>
    loadsSelectors.getLoad(state, shipmentKey, loadKey)
  );
  const currentLoadType = currentLoad[loadConstants.FIELDS.LOAD_TYPE];
  const { BULK, PARCEL, PALLET } = loadConstants.LOAD_TYPES;

  if (value === loadConstants.LOAD_TYPES.PALLET && currentLoadType === PALLET)
    return;
  if (
    value === loadConstants.LOAD_TYPES.NON_PALLETIZED &&
    (currentLoadType === BULK || currentLoadType === PARCEL)
  )
    return;

  let newValues = {
    [field]: value,
    [loadConstants.FIELDS.ITEM_HEIGHT]: undefined,
    [loadConstants.FIELDS.ITEM_WIDTH]: undefined,
    [loadConstants.FIELDS.ITEM_LENGTH]: undefined,
    [loadConstants.FIELDS.TOTAL_WEIGHT]: undefined,
    [loadConstants.FIELDS.LOAD_TYPE]: undefined,
  };

  if (value === loadConstants.LOAD_TYPES.PALLET) {
    const newPalletType = loadConstants.PALLET_TYPES.EU_PALLET;
    newValues[loadConstants.FIELDS.PALLET_TYPE] = newPalletType;
    newValues[loadConstants.FIELDS.LOAD_TYPE] = PALLET;
    const defaultPalletSize = yield select(
      loadsSelectors.getDefaultPalletSize,
      newPalletType
    );
    newValues = { ...newValues, ...defaultPalletSize };
  }
  if (value === loadConstants.LOAD_TYPES.NON_PALLETIZED) {
    const newPalletType = loadConstants.PALLET_TYPES.CUSTOM_PALLET;
    newValues[loadConstants.FIELDS.PALLET_TYPE] = newPalletType;
    newValues[loadConstants.FIELDS.LOAD_TYPE] = PARCEL;
    const defaultParcelSize = yield select(
      loadsSelectors.getDefaultNonPalletSize,
      loadConstants.NON_PALLETIZED_TYPES.PARCEL_STANDART
    );
    newValues = { ...newValues, ...defaultParcelSize };
  }
  yield put(
    loadsActions.changeLoadValues({
      shipmentKey,
      loadKey,
      values: newValues,
    })
  );
  yield call(handleGetMaximum);
  yield put(loadsActions.validateForm());
  yield put(orderActions.fetchQuote());
  yield put(orderActions.fetchTimeWindows());
}

function* handleNonPalletType({ payload }) {
  const { field, value, shipmentKey, loadKey } = payload;
  const { PARCEL, BULK } = loadConstants.LOAD_TYPES;
  const getDefaultNonPalletSize = yield select(
    loadsSelectors.getDefaultNonPalletSize,
    value
  );
  let newNonPalletSize = {
    [loadConstants.FIELDS.ITEM_WIDTH]: undefined,
    [loadConstants.FIELDS.ITEM_LENGTH]: undefined,
    [loadConstants.FIELDS.ITEM_HEIGHT]: undefined,
  };
  newNonPalletSize = { ...newNonPalletSize, ...getDefaultNonPalletSize };

  yield put(
    loadsActions.changeLoadValues({
      shipmentKey,
      loadKey,
      values: {
        [field]: value.startsWith(PARCEL) ? PARCEL : BULK,
        ...newNonPalletSize,
      },
    })
  );
  yield call(handleGetMaximum);
  yield put(loadsActions.validateForm());
  yield put(orderActions.fetchQuote());
  yield put(orderActions.fetchTimeWindows());
}

function* handlePalletType({ payload }) {
  const { field, value, shipmentKey, loadKey } = payload;

  const defaultPalletSize = yield select(
    loadsSelectors.getDefaultPalletSize,
    value
  );
  let newPalletSize = {
    [loadConstants.FIELDS.ITEM_WIDTH]: undefined,
    [loadConstants.FIELDS.ITEM_LENGTH]: undefined,
  };
  newPalletSize = { ...newPalletSize, ...defaultPalletSize };

  yield put(
    loadsActions.changeLoadValues({
      shipmentKey,
      loadKey,
      values: {
        [field]: value,
        ...newPalletSize,
      },
    })
  );
  yield call(handleGetMaximum);
  yield put(loadsActions.validateForm());
  yield put(orderActions.fetchQuote());
  yield put(orderActions.fetchTimeWindows());
}

function* handleFocusLoadValue({ payload }) {
  const { shipmentKey, loadKey } = payload;
  yield call(handleGetMaximum, { shipmentKey, loadKey });
}

function* handleBlurLoadValue({ payload }) {
  const { shipmentKey, loadKey } = payload;
  yield call(handleGetMaximum, { shipmentKey, loadKey });
}

function* handleGetMaximum() {
  const orderType = yield select(orderSelectors.getTypeOfOrder);

  let shipmentsInOrder = yield select(
    selectorsByType.shipments[orderType].getOrderOfShipments
  );
  const shipmentsWithLoads = yield select(
    orderSelectors.getShipmentsOnlyWithLoadsInForm
  );

  const settings = yield select(settingsSelectors.getCountrySettings);
  const validations = yield select(orderSelectors.getValidations);
  const shipmentsWithCorrectLoads = {};
  const shipmentKeys = Object.keys(shipmentsWithLoads);
  shipmentKeys.forEach((sKey) => {
    const shipment = shipmentsWithLoads[sKey];
    const loadKeys = Object.keys(shipment);
    const purgedShipmentLoads = {};
    loadKeys.forEach((lKey) => {
      const load = shipmentsWithLoads[sKey][lKey];

      const { errors } = loadValidators.validateLoad({
        load,
        settings,
        validations,
        orderType,
        onlyDirty: false,
      });
      const isLoadValid =
        !errors.hasOwnProperty(loadConstants.FIELDS.ITEM_LENGTH) &&
        !errors.hasOwnProperty(loadConstants.FIELDS.ITEM_WIDTH) &&
        !errors.hasOwnProperty(loadConstants.FIELDS.ITEM_HEIGHT) &&
        !errors.hasOwnProperty(loadConstants.FIELDS.NUMBER_OF_ITEMS);

      if (isLoadValid) {
        purgedShipmentLoads[lKey] = load;
      }
    });
    if (Object.keys(purgedShipmentLoads).length > 0) {
      shipmentsWithCorrectLoads[sKey] = purgedShipmentLoads;
    }
  });
  const canGetMaximum = Object.keys(shipmentsWithCorrectLoads).length > 0;

  if (!canGetMaximum) return false;

  const shipmentsWithCorrectLoadsKeys = Object.keys(shipmentsWithCorrectLoads);
  const shipmentsInOrderWithCorrectLoads = shipmentsInOrder.filter((s) =>
    shipmentsWithCorrectLoadsKeys.some((k) => s.includes(k))
  );

  try {
    const maxNumberOfThisType = yield call(loadMethods.getMaximumOfLoads, {
      shipmentsInOrder: shipmentsInOrderWithCorrectLoads,
      shipmentsWithLoads: shipmentsWithCorrectLoads,
      maxEuPallets: null,
    });

    if (!maxNumberOfThisType) return false;

    yield put(orderActions.setMaximums({ maximums: maxNumberOfThisType }));

    return maxNumberOfThisType;
  } catch (err) {
    console.error(err);
    return false;
  }
}

function* isMaxReached() {
  const shipmentsWithLoads = yield select(
    orderSelectors.getShipmentsWithLoadsInForm
  );
  const maximums = yield call(handleGetMaximum);
  const maxReached = Object.keys(shipmentsWithLoads).some((shipmentKey) => {
    const loadsInShipmenet = shipmentsWithLoads[shipmentKey].loads;
    return Object.keys(loadsInShipmenet).some(
      (loadkey) =>
        loadsInShipmenet &&
        loadsInShipmenet[loadkey] &&
        loadsInShipmenet[loadkey][loadConstants.FIELDS.NUMBER_OF_ITEMS] &&
        maximums &&
        maximums[shipmentKey] &&
        maximums[shipmentKey][loadkey] &&
        loadsInShipmenet[loadkey][loadConstants.FIELDS.NUMBER_OF_ITEMS] >=
          maximums[shipmentKey][loadkey]
    );
  });
  yield put(orderActions.setMaxReached(!!maxReached));
}

function* handleValidateForm() {
  const settings = yield select(settingsSelectors.getCountrySettings);
  const validations = yield select(orderSelectors.getValidations);
  const orderType = yield select(orderSelectors.getTypeOfOrder);
  const loads = yield select(orderSelectors.getLoadsInForm);
  const maxItemsInLoads = yield select(orderSelectors.getMaxItemsInLoads);

  const totalWeight = loads.reduce(
    (total, load) => total + load.total_weight,
    0
  );

  yield put(errorActions.cleanErrors());
  const loadErrors = loads
    .reduce((current, load) => {
      const { errors } = loadValidators.validateLoad({
        load,
        settings,
        validations,
        orderType,
        onlyDirty: true,
      });
      const inputKeys = Object.values(errors).map((errorField) =>
        keyGenerator.load({
          shipmentKey: load.shipmentKey,
          loadKey: load.loadKey,
          field: errorField,
        })
      );
      if (
        maxItemsInLoads?.[load.shipmentKey]?.[load.loadKey] <
        load.number_of_items
      ) {
        inputKeys.push(
          keyGenerator.load({
            shipmentKey: load.shipmentKey,
            loadKey: load.loadKey,
            field: loadConstants.FIELDS.NUMBER_OF_ITEMS,
          })
        );
      }

      if (totalWeight > settings.maximum_truck_weight) {
        inputKeys.push(
          keyGenerator.load({
            shipmentKey: load.shipmentKey,
            loadKey: load.loadKey,
            field: loadConstants.FIELDS.TOTAL_WEIGHT,
          })
        );
      }

      return [...current, ...inputKeys];
    }, [])
    .reduce((current, inputKey) => ({ ...current, [inputKey]: 'error' }), {});
  yield fork(isMaxReached);
  yield put(errorActions.setError(loadErrors));
}

export default function* () {
  yield takeLatest(types.createLoad$, handleLoadAdd);
  yield takeLatest(types.deleteLoad$, handleLoadRemove);

  yield takeLatest(types.changeLoadValue$, handleLoadChange);
  yield takeLatest(types.changeLoadType$, handleLoadType);
  yield takeLatest(types.changePalletType$, handlePalletType);
  yield takeLatest(types.changeNonPalletType$, handleNonPalletType);

  yield takeLatest(types.onFocusLoadValue$, handleFocusLoadValue);
  yield takeLatest(types.onBlurLoadValue$, handleBlurLoadValue);

  yield takeLatest(types.validateForm$, handleValidateForm);
}

export {
  handleLoadAdd,
  handleLoadRemove,
  handleLoadChange,
  handlePalletType,
  handleFocusLoadValue,
  handleBlurLoadValue,
  handleGetMaximum,
  isMaxReached,
  handleValidateForm,
};
